import type { CSSProperties } from 'react';
import { scaleOf, shareOnScale, type ValueScale } from '../ColumnChart/chart-scale.ts';
import { positionOf } from '../Chart/BucketLabels.tsx';
import { ChartFigure, PlotFrame } from '../Chart/ChartFrame.tsx';
import { ChartTooltip, type TooltipRow } from '../Chart/ChartTooltip.tsx';
import { bucketsOf, hasValues, valuesOf, type ChartSeries, type SeriesKey } from '../Chart/chart-series.ts';
import { useBucketFocus } from '../Chart/use-bucket-focus.ts';
import chartStyles from '../Chart/Chart.module.css';
import { EmptyState } from '../EmptyState/EmptyState.tsx';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import { seriesColorOf } from '../../tokens/chart.ts';
import { areaPathOf, linePathOf, VIEW_SIZE } from './line-geometry.ts';
import styles from './LineChart.module.css';

type LineVariant = 'line' | 'area';

export type LineChartProps = {
  label: string;
  series: readonly ChartSeries[];
  comparison?: ChartSeries | undefined;
  variant?: LineVariant;
  formatTick: (value: number) => string;
  emptyTitle: string;
  loading?: boolean;
};

const COMPARISON_COLOR = 'var(--color-muted)';

type Plotted = { readonly entry: ChartSeries; readonly color: string; readonly dashed: boolean };

const plottedOf = (
  series: readonly ChartSeries[],
  comparison: ChartSeries | undefined,
): readonly Plotted[] => [
  ...(comparison === undefined ? [] : [{ entry: comparison, color: COMPARISON_COLOR, dashed: true }]),
  ...series.map((entry, index) => ({ entry, color: seriesColorOf(entry.key, index), dashed: false })),
];

const legendOf = (plotted: readonly Plotted[]): readonly SeriesKey[] =>
  plotted.map(({ entry, color, dashed }) => ({
    key: entry.key,
    label: entry.label,
    color,
    mark: dashed ? 'dash' : 'line',
  }));

const tooltipRowsOf = (plotted: readonly Plotted[], index: number): readonly TooltipRow[] =>
  plotted.flatMap(({ entry, color, dashed }) => {
    const point = entry.points[index];
    if (point === undefined) return [];
    return [
      {
        key: entry.key,
        label: dashed ? `${entry.label} · ${point.label}` : entry.label,
        display: point.display,
        color,
        dashed,
      },
    ];
  });

const markerStyle = (
  scale: ValueScale,
  value: number,
  index: number,
  count: number,
  color: string,
): CSSProperties =>
  ({
    '--chart-x': String(positionOf(index, count)),
    '--chart-at': String(shareOnScale(scale, value)),
    color,
  }) as CSSProperties;

const Paths = ({
  plotted,
  scale,
  variant,
}: {
  plotted: readonly Plotted[];
  scale: ValueScale;
  variant: LineVariant;
}) => (
  <svg
    className={styles['paths']}
    viewBox={`0 0 ${String(VIEW_SIZE)} ${String(VIEW_SIZE)}`}
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    {variant === 'area'
      ? plotted
          .filter(({ dashed }) => !dashed)
          .map(({ entry, color }) => (
            <path
              key={`area-${entry.key}`}
              className={styles['area']}
              d={areaPathOf(entry, scale)}
              style={{ fill: color }}
            />
          ))
      : null}
    {plotted.map(({ entry, color, dashed }) => (
      <path
        key={entry.key}
        className={styles['line']}
        data-dashed={dashed ? true : undefined}
        d={linePathOf(entry, scale)}
        style={{ stroke: color }}
      />
    ))}
  </svg>
);

const Markers = ({
  plotted,
  scale,
  index,
}: {
  plotted: readonly Plotted[];
  scale: ValueScale;
  index: number;
}) =>
  plotted.map(({ entry, color }) => {
    const point = entry.points[index];
    return point === undefined || point.missing === true ? null : (
      <span
        key={entry.key}
        className={chartStyles['dot']}
        style={markerStyle(scale, point.value, index, entry.points.length, color)}
      />
    );
  });

const Focus = ({
  plotted,
  scale,
  index,
  count,
}: {
  plotted: readonly Plotted[];
  scale: ValueScale;
  index: number;
  count: number;
}) => {
  const position = positionOf(index, count);
  const title = plotted.at(-1)?.entry.points[index]?.label ?? '';
  return (
    <>
      <span className={chartStyles['crosshair']} style={{ '--chart-x': String(position) } as CSSProperties} />
      <Markers plotted={plotted} scale={scale} index={index} />
      <ChartTooltip title={title} rows={tooltipRowsOf(plotted, index)} position={position} />
    </>
  );
};

const LastMarkers = ({ plotted, scale }: { plotted: readonly Plotted[]; scale: ValueScale }) =>
  plotted
    .filter(({ dashed }) => !dashed)
    .map(({ entry, color }) => {
      const count = entry.points.length;
      const last = entry.points.at(-1);
      return last === undefined || last.missing === true ? null : (
        <span
          key={entry.key}
          className={chartStyles['dot']}
          style={markerStyle(scale, last.value, count - 1, count, color)}
        />
      );
    });

const Plot = ({
  label,
  series,
  comparison,
  variant,
  formatTick,
}: Required<Omit<LineChartProps, 'loading' | 'emptyTitle' | 'comparison'>> & {
  comparison: ChartSeries | undefined;
}) => {
  const plotted = plottedOf(series, comparison);
  const scale = scaleOf(valuesOf(plotted.map(({ entry }) => entry)));
  const buckets = bucketsOf(series);
  const focus = useBucketFocus(buckets.length, 'between');
  return (
    <PlotFrame
      label={label}
      scale={scale}
      formatTick={formatTick}
      buckets={buckets}
      placeAt={positionOf}
      plotProps={focus.plotProps}
    >
      <Paths plotted={plotted} scale={scale} variant={variant} />
      {focus.active === null ? (
        <LastMarkers plotted={plotted} scale={scale} />
      ) : (
        <Focus plotted={plotted} scale={scale} index={focus.active} count={buckets.length} />
      )}
    </PlotFrame>
  );
};

export const LineChart = ({
  label,
  series,
  comparison,
  variant = 'line',
  formatTick,
  emptyTitle,
  loading = false,
}: LineChartProps) => {
  if (loading) return <Skeleton height="18rem" />;
  if (!hasValues(series) && (comparison === undefined || !hasValues([comparison])))
    return <EmptyState title={emptyTitle} />;
  const plotted = plottedOf(series, comparison);
  return (
    <ChartFigure label={label} keys={legendOf(plotted)} series={plotted.map(({ entry }) => entry)}>
      <Plot label={label} series={series} comparison={comparison} variant={variant} formatTick={formatTick} />
    </ChartFigure>
  );
};
